import React, { useState, useEffect, useRef } from 'react';

// ──────────────────────────────────────────────
// INDI Landing Page
// Public-facing marketing page for indi.space
// Full platform accessible at /platform route
// ──────────────────────────────────────────────

export default function LandingPage({ onEnterPlatform, onPrivacy, onTerms }) {
  const [email, setEmail] = useState('');
  const [formType, setFormType] = useState(null); // 'filmmaker' | 'investor'
  const [submitted, setSubmitted] = useState({});
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const registerRef = (id) => (el) => {
    if (el) sectionRefs.current[id] = el;
  };

    const isVisible = (id) => visibleSections.has(id);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const handleWaitlist = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'community', email }),
      });
      const result = await response.json();
      if (result.success) {
        setSubmitted((prev) => ({ ...prev, waitlist: true }));
        setEmail('');
      } else {
        console.error('Waitlist error:', result.error);
      }
    } catch (error) {
      console.error('Waitlist error:', error);
      // Still show success to user even if backend is down
      setSubmitted((prev) => ({ ...prev, waitlist: true }));
      setEmail('');
    }
  };

  const handleFormSubmit = async (e, type, data) => {
    e.preventDefault();
    const isFromWaitlist = formType === 'waitlist';
    try {
      const response = await fetch(`${API_URL}/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, ...data }),
      });
      const result = await response.json();
      if (result.success) {
        setSubmitted((prev) => ({ ...prev, [type]: true, waitlist: true }));
        // Only close the modal for standalone filmmaker submissions, not waitlist
        if (!isFromWaitlist) setFormType(null);
      } else {
        console.error('Submission error:', result.error);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitted((prev) => ({ ...prev, [type]: true, waitlist: true }));
      if (!isFromWaitlist) setFormType(null);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060A13',
      color: '#E8ECF1',
      fontFamily: "'Instrument Sans', 'Helvetica Neue', sans-serif",
      overflowX: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .indi-fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .indi-fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .indi-stagger-1 { transition-delay: 0.1s; }
        .indi-stagger-2 { transition-delay: 0.2s; }
        .indi-stagger-3 { transition-delay: 0.3s; }
        .indi-stagger-4 { transition-delay: 0.4s; }

        .indi-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
          opacity: 0.4;
        }

        .indi-btn {
          padding: 0.85rem 2rem;
          border: none;
          border-radius: 10px;
          font-family: 'Instrument Sans', sans-serif;
          font-weight: 600;
          font-size: 0.9375rem;
          cursor: pointer;
          transition: all 300ms cubic-bezier(0.16, 1, 0.3, 1);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .indi-btn-primary {
          background: #06B6D4;
          color: #060A13;
        }
        .indi-btn-primary:hover {
          background: #22D3EE;
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(6, 182, 212, 0.3);
        }
        .indi-btn-secondary {
          background: rgba(6, 182, 212, 0.08);
          color: #06B6D4;
          border: 1px solid rgba(6, 182, 212, 0.2);
        }
        .indi-btn-secondary:hover {
          background: rgba(6, 182, 212, 0.15);
          border-color: rgba(6, 182, 212, 0.4);
          transform: translateY(-2px);
        }
        .indi-btn-lime {
          background: #84CC16;
          color: #060A13;
        }
        .indi-btn-lime:hover {
          background: #A3E635;
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(132, 204, 22, 0.3);
        }

        .indi-input {
          width: 100%;
          padding: 0.8rem 1rem;
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 10px;
          color: #E8ECF1;
          font-family: 'Instrument Sans', sans-serif;
          font-size: 0.9375rem;
          outline: none;
          transition: border-color 250ms ease, box-shadow 250ms ease;
        }
        .indi-input:focus {
          border-color: rgba(6, 182, 212, 0.4);
          box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.08);
        }
        .indi-input::placeholder {
          color: #64748B;
        }

        .indi-textarea {
          min-height: 100px;
          resize: vertical;
          line-height: 1.55;
        }

        .indi-card {
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.5) 100%);
          border: 1px solid rgba(255, 255, 255, 0.04);
          border-radius: 16px;
          padding: 2rem;
          transition: border-color 400ms ease, transform 400ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .indi-card:hover {
          border-color: rgba(6, 182, 212, 0.15);
          transform: translateY(-4px);
        }

        @media (max-width: 768px) {
          .indi-hero-title { font-size: 2.5rem !important; }
          .indi-grid-2 { grid-template-columns: 1fr !important; }
          .indi-grid-3 { grid-template-columns: 1fr !important; }
          .indi-section { padding: 4rem 1.25rem !important; }
          .indi-nav-inner { padding: 0 1.25rem !important; }
        }

        .indi-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          background: rgba(6, 10, 19, 0.85);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        }

.indi-timeline-line {
          position: absolute;
          left: -32px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(180deg, rgba(6, 182, 212, 0.3) 0%, rgba(132, 204, 22, 0.3) 100%);
        }

.indi-timeline-dot {
          position: absolute;
          left: -40px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid;
          background: #060A13;
        }

        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(6, 10, 19, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          padding: 1rem;
        }
      `}</style>

      {/* ── Navigation ──────────────────────────────── */}
      <nav className="indi-nav">
        <div className="indi-nav-inner" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '1.5rem',
            fontWeight: '800',
            letterSpacing: '-0.03em',
            background: 'linear-gradient(135deg, #06B6D4 0%, #84CC16 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            INDI
          </div>
          <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            {['Vision', 'How It Works', 'Roadmap'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/ /g, '-')}`}
                style={{
                  color: '#94A3B8',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'color 200ms',
                }}
                onMouseEnter={(e) => e.target.style.color = '#E8ECF1'}
                onMouseLeave={(e) => e.target.style.color = '#94A3B8'}
              >
                {item}
              </a>
            ))}
            <button
              className="indi-btn indi-btn-primary"
              style={{ padding: '0.55rem 1.25rem', fontSize: '0.8125rem' }}
              onClick={() => {
                document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────── */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6rem 2rem 4rem',
        overflow: 'hidden',
      }}>
        {/* Atmospheric glows */}
        <div className="indi-glow" style={{ width: '600px', height: '600px', background: 'rgba(6, 182, 212, 0.08)', top: '-200px', right: '-200px' }} />
        <div className="indi-glow" style={{ width: '500px', height: '500px', background: 'rgba(132, 204, 22, 0.05)', bottom: '-150px', left: '-150px' }} />

        {/* Grain texture overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E")`,
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '800px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{
            marginBottom: '0.25rem',
            opacity: 0,
            animation: 'fadeUp 0.8s 0.1s forwards',
            textAlign: 'center',
          }}>
            <img
              src="/assets/indi-logo-email-dark.png"
              alt="INDI"
              style={{ width: '400px', height: 'auto', display: 'inline-block' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '1rem',
            fontWeight: '500',
            color: '#06B6D4',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '1.5rem',
            opacity: 0,
            animation: 'fadeUp 0.8s 0.2s forwards',
          }}>
            The Future of Independent Film
          </div>

          <h1
            className="indi-hero-title"
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '4rem',
              fontWeight: '800',
              lineHeight: '1.05',
              letterSpacing: '-0.04em',
              marginBottom: '1.5rem',
              opacity: 0,
              animation: 'fadeUp 0.8s 0.4s forwards',
            }}
          >
            Fund Films.{' '}
            <span style={{
              background: 'linear-gradient(135deg, #06B6D4 0%, #84CC16 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Change the Model.
            </span>
          </h1>

          <p style={{
            fontSize: '1.125rem',
            lineHeight: '1.7',
            color: '#94A3B8',
            maxWidth: '600px',
            margin: '0 auto 2.5rem',
            opacity: 0,
            animation: 'fadeUp 0.8s 0.6s forwards',
          }}>
            INDI is building a SEC-compliant crowdfunding platform 
            that will connect investors with independent filmmakers. 
            Join the waitlist to get early access.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            opacity: 0,
            animation: 'fadeUp 0.8s 0.8s forwards',
          }}>
            <button
              className="indi-btn indi-btn-primary"
              onClick={() => setFormType('waitlist')}
            >
              Join the Waitlist
            </button>
          </div>

          {/* Scroll indicator */}
          <div style={{
            marginTop: '4rem',
            opacity: 0,
            animation: 'fadeUp 0.8s 1.2s forwards',
          }}>
            <div style={{
              width: '1px',
              height: '48px',
              background: 'linear-gradient(180deg, rgba(6, 182, 212, 0.4), transparent)',
              margin: '0 auto',
              animation: 'pulse 2s infinite',
            }} />
          </div>
        </div>

        <style>{`
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(24px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 1; }
          }
        `}</style>
      </section>

      {/* ── Vision / Key Differentiators ──────────────── */}
      <section
        id="vision"
        ref={registerRef('vision')}
        className="indi-section"
        style={{ padding: '6rem 2rem', maxWidth: '1200px', margin: '0 auto' }}
      >
        <div className={`indi-fade-in ${isVisible('vision') ? 'visible' : ''}`}>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.75rem',
            color: '#06B6D4',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '1rem',
          }}>
            Why INDI
          </div>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '2.25rem',
            fontWeight: '700',
            letterSpacing: '-0.03em',
            marginBottom: '1rem',
            lineHeight: '1.15',
          }}>
            A new model for{' '}
            <span style={{ color: '#06B6D4' }}>independent film</span>
          </h2>
          <p style={{
            fontSize: '1.0625rem',
            lineHeight: '1.7',
            color: '#94A3B8',
            maxWidth: '640px',
            marginBottom: '3rem',
          }}>
            Traditional film financing shuts out investors and filmmakers alike.
            INDI opens the door with regulatory compliance, transparent revenue sharing,
            and end-to-end support from raise through distribution.
          </p>
        </div>

        <div
          className="indi-grid-3"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}
        >
          {[
            {
              icon: '🛡️',
              title: 'Designed for SEC Compliance',
              desc: 'Built around Regulation CF from day one. Every project will undergo a comprehensive compliance intake process. We\'re engaging securities counsel to validate our framework. This is regulated investment, done right.',
              color: '#06B6D4',
              delay: 1,
            },
            {
              icon: '🔗',
              title: 'Transparent Revenue Sharing',
              desc: 'Investors get automated revenue distributions and clear, real-time reporting as films earn — with built-in infrastructure for future secondary-market liquidity. Sign in with email; no wallet required.',
              footer: 'Powered by Solana',
              color: '#84CC16',
              delay: 2,
            },
            {
              icon: '🎬',
              title: 'Integrated Distribution',
              desc: 'INDI Distribution LLC handles festival strategy, distributor placement, revenue collection, and investor reporting. Filmmakers create. We handle the business.',
              color: '#06B6D4',
              delay: 3,
            },
          ].map((item) => (
            <div
              key={item.title}
              className={`indi-card indi-fade-in indi-stagger-${item.delay} ${isVisible('vision') ? 'visible' : ''}`}
            >
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{item.icon}</div>
              <h3 style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '1.125rem',
                fontWeight: '700',
                marginBottom: '0.75rem',
                color: item.color,
              }}>
                {item.title}
              </h3>
              <p style={{ fontSize: '0.9375rem', lineHeight: '1.65', color: '#8892A4' }}>
                {item.desc}
              </p>
              {item.footer && (
                <div style={{
                  marginTop: '1rem',
                  paddingTop: '0.75rem',
                  borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.6875rem',
                  color: '#64748B',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}>
                  {item.footer}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────── */}
      <section
        id="how-it-works"
        ref={registerRef('how-it-works')}
        className="indi-section"
        style={{
          padding: '6rem 2rem',
          background: 'linear-gradient(180deg, transparent 0%, rgba(6, 182, 212, 0.02) 50%, transparent 100%)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div className={`indi-fade-in ${isVisible('how-it-works') ? 'visible' : ''}`}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.75rem',
              color: '#84CC16',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '1rem',
            }}>
              The Process
            </div>
            <h2 style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '2.25rem',
              fontWeight: '700',
              letterSpacing: '-0.03em',
              marginBottom: '3.5rem',
              lineHeight: '1.15',
            }}>
              How INDI works
            </h2>
          </div>

          <div className="indi-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* For Filmmakers */}
            <div className={`indi-card indi-fade-in indi-stagger-1 ${isVisible('how-it-works') ? 'visible' : ''}`}>
              <div style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '0.8125rem',
                fontWeight: '600',
                color: '#84CC16',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '1.5rem',
              }}>
                For Filmmakers
              </div>
              {[
                { num: '01', text: 'Submit your project through our comprehensive intake process' },
                { num: '02', text: 'INDI team reviews for quality and compliance readiness' },
                { num: '03', text: 'Launch your crowdfunding campaign to qualified investors' },
                { num: '04', text: 'INDI Distribution handles the rest — festivals, sales, revenue' },
              ].map((step) => (
                <div key={step.num} style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1.25rem',
                  alignItems: 'flex-start',
                }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.75rem',
                    color: '#84CC16',
                    fontWeight: '500',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}>
                    {step.num}
                  </span>
                  <span style={{ fontSize: '0.9375rem', lineHeight: '1.55', color: '#B0B8C8' }}>
                    {step.text}
                  </span>
                </div>
              ))}
            </div>

            {/* For Investors */}
            <div className={`indi-card indi-fade-in indi-stagger-2 ${isVisible('how-it-works') ? 'visible' : ''}`}>
              <div style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '0.8125rem',
                fontWeight: '600',
                color: '#06B6D4',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '1.5rem',
              }}>
                For Investors
              </div>
              {[
                { num: '01', text: 'Sign up with just an email' },
                { num: '02', text: 'Browse vetted film projects with detailed financials and team info' },
                { num: '03', text: 'Invest starting at $25 when platform launches' },
                { num: '04', text: 'Transparent revenue tracking and automated distributions as films earn' },
              ].map((step) => (
                <div key={step.num} style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1.25rem',
                  alignItems: 'flex-start',
                }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.75rem',
                    color: '#06B6D4',
                    fontWeight: '500',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}>
                    {step.num}
                  </span>
                  <span style={{ fontSize: '0.9375rem', lineHeight: '1.55', color: '#B0B8C8' }}>
                    {step.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Roadmap ──────────────────────────────── */}
      <section
        id="roadmap"
        ref={registerRef('roadmap')}
        className="indi-section"
        style={{ padding: '6rem 2rem', maxWidth: '900px', margin: '0 auto' }}
      >
        <div className={`indi-fade-in ${isVisible('roadmap') ? 'visible' : ''}`}>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.75rem',
            color: '#06B6D4',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '1rem',
          }}>
            Roadmap
          </div>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '2.25rem',
            fontWeight: '700',
            letterSpacing: '-0.03em',
            marginBottom: '3.5rem',
            lineHeight: '1.15',
          }}>
            Where we're headed
          </h2>
        </div>

        <div style={{ position: 'relative', paddingLeft: '48px' }}>
          <div className="indi-timeline-line" />

          {[
            {
              phase: 'Q1-Q2 2026',
              title: 'Foundation',
              status: 'active',
              items: ['Platform architecture and SEC compliance framework', 'Investor ownership and revenue-distribution infrastructure', 'Strategic partnerships with securities counsel', 'Seed funding campaign and initial team hires'],
              color: '#06B6D4',
            },
            {
              phase: 'Q3-Q4 2026',
              title: 'Private Beta',
              status: 'upcoming',
              items: ['Invite-only filmmaker submissions', 'First Reg CF filings with partner attorneys', 'Investor onboarding with KYC integration'],
              color: '#06B6D4',
            },
            {
              phase: 'Q1-Q2 2027',
              title: 'Public Launch',
              status: 'upcoming',
              items: ['Open filmmaker submissions with INDI team curation', 'Notable Creator Reserve program launch', 'INDI Distribution LLC operational for funded projects', 'Public investment campaigns'],
              color: '#84CC16',
            },
            {
              phase: 'Q3-Q4 2027',
              title: 'Scale',
              status: 'future',
              items: ['INDI streaming platform with subscription revenue', 'Secondary marketplace for token trading (pending ATS registration)', 'Community-driven project curation — INDI members vote on which submissions advance to fundraising',],
              color: '#84CC16',
            },
          ].map((phase, i) => (
            <div
              key={phase.phase}
              className={`indi-fade-in indi-stagger-${i + 1} ${isVisible('roadmap') ? 'visible' : ''}`}
              style={{ position: 'relative', marginBottom: '3rem' }}
            >
              <div
                className="indi-timeline-dot"
                style={{
                  borderColor: phase.color,
                  top: '4px',
                  background: phase.status === 'active' ? phase.color : '#060A13',
                }}
              />
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.75rem',
                color: phase.color,
                fontWeight: '500',
                marginBottom: '0.4rem',
              }}>
                {phase.phase}
                {phase.status === 'active' && (
                  <span style={{
                    marginLeft: '0.75rem',
                    padding: '0.15rem 0.5rem',
                    background: `${phase.color}15`,
                    border: `1px solid ${phase.color}30`,
                    borderRadius: '4px',
                    fontSize: '0.6875rem',
                  }}>
                    IN PROGRESS
                  </span>
                )}
              </div>
              <h3 style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: '1.25rem',
                fontWeight: '700',
                marginBottom: '0.75rem',
                color: '#E8ECF1',
              }}>
                {phase.title}
              </h3>
              {phase.items.map((item, j) => (
                <div key={j} style={{
                  fontSize: '0.9375rem',
                  color: '#8892A4',
                  lineHeight: '1.55',
                  marginBottom: '0.35rem',
                  paddingLeft: '1rem',
                  position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    color: phase.status === 'active' ? phase.color : '#64748B',
                  }}>
                    {phase.status === 'active' ? '→' : '·'}
                  </span>
                  {item}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── Waitlist / CTA ──────────────────────────────── */}
      <section
        id="waitlist"
        ref={registerRef('waitlist')}
        className="indi-section"
        style={{
          padding: '6rem 2rem',
          background: 'linear-gradient(180deg, transparent 0%, rgba(6, 182, 212, 0.03) 100%)',
        }}
      >
        <div
          className={`indi-fade-in ${isVisible('waitlist') ? 'visible' : ''}`}
          style={{ maxWidth: '560px', margin: '0 auto', textAlign: 'center' }}
        >
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '2.25rem',
            fontWeight: '700',
            letterSpacing: '-0.03em',
            marginBottom: '1rem',
            lineHeight: '1.15',
          }}>
            Join the INDI Community
          </h2>
          <p style={{
            fontSize: '1.0625rem',
            lineHeight: '1.65',
            color: '#94A3B8',
            marginBottom: '2rem',
          }}>
            Be among the first to access INDI. Community members get 
            priority updates, early access, and a front-row seat as 
            we reshape independent film financing.
          </p>

          {submitted.waitlist ? (
            <div style={{
              padding: '1.25rem',
              background: 'rgba(132, 204, 22, 0.08)',
              border: '1px solid rgba(132, 204, 22, 0.2)',
              borderRadius: '12px',
              color: '#84CC16',
              fontWeight: '600',
            }}>
              Welcome to the INDI community. We'll be in touch soon.
            </div>
          ) : (
            <button
              className="indi-btn indi-btn-primary"
              onClick={() => setFormType('waitlist')}
              style={{ fontSize: '1rem', padding: '0.9rem 2.5rem' }}
            >
              Join the Waitlist
            </button>
          )}
        </div>
      </section>

      {/* ── Footer ──────────────────────────────── */}
      <footer style={{
        padding: '3rem 2rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.04)',
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
        }}>
          <div>
            <div style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '1.25rem',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #06B6D4 0%, #84CC16 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.35rem',
            }}>
              INDI
            </div>
            <div style={{
              fontSize: '0.8125rem',
              color: '#8892A4',
            }}>
              Democratizing Independent Film Financing
            </div>
          </div>
                  <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
            <button onClick={onPrivacy} style={{ background: 'none', border: 'none', color: '#8892A4', fontSize: '0.75rem', fontFamily: "'Instrument Sans', sans-serif", cursor: 'pointer' }}>
              Privacy Policy
            </button>
            <button onClick={onTerms} style={{ background: 'none', border: 'none', color: '#8892A4', fontSize: '0.75rem', fontFamily: "'Instrument Sans', sans-serif", cursor: 'pointer' }}>
              Terms of Service
            </button>
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#8892A4',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            © 2026 INDI Platform. All rights reserved.
          </div>
          <div style={{
            width: '100%',
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.04)',
            fontSize: '0.6875rem',
            color: '#4A5568',
            lineHeight: '1.6',
            textAlign: 'center',
          }}>
            INDI Platform is currently in development. No securities are being offered or sold at this time. 
            All investment-related features described on this site represent planned functionality subject to regulatory approval.
          </div>
        </div>
      </footer>

      {/* ── Waitlist Modal ──────────────── */}
      {formType === 'waitlist' && (
        <WaitlistModal
          onClose={() => setFormType(null)}
          onSubmit={handleFormSubmit}
          onFilmmakerApply={() => setFormType('filmmaker')}
          submitted={submitted.waitlist}
        />
      )}

      {/* ── Filmmaker Application Modal ──────────────── */}
      {formType === 'filmmaker' && (
        <FilmmakerModal
          onClose={() => setFormType(null)}
          onSubmit={handleFormSubmit}
          submitted={submitted.filmmaker}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// Waitlist Modal
// ──────────────────────────────────────────────

function WaitlistModal({ onClose, onSubmit, onFilmmakerApply, submitted }) {
  const emptyProject = { projectTitle: '', genre: '', budget: '', synopsis: '' };
  const [data, setData] = useState({
    email: '', name: '', roles: [], wantsToSubmitProject: false,
    portfolio: '',
    projects: [{ ...emptyProject }],
  });

  const toggleRole = (role) => {
    setData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
      // Reset project fields if filmmaker is unchecked
      ...(!prev.roles.includes(role) ? {} : role === 'filmmaker' ? { wantsToSubmitProject: false } : {}),
    }));
  };

  const addProject = () => {
    if (data.projects.length < 3) {
      setData({ ...data, projects: [...data.projects, { ...emptyProject }] });
    }
  };

  const removeProject = (index) => {
    if (data.projects.length > 1) {
      setData({ ...data, projects: data.projects.filter((_, i) => i !== index) });
    }
  };

  const updateProject = (index, field, value) => {
    const updated = [...data.projects];
    updated[index] = { ...updated[index], [field]: value };
    setData({ ...data, projects: updated });
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.8125rem',
    color: '#94A3B8',
    marginBottom: '0.35rem',
    fontWeight: '600',
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="indi-card" style={{ maxWidth: '500px', width: '100%', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
          <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.375rem', fontWeight: '700', marginBottom: '0.5rem' }}>
            Welcome to INDI
          </h3>
          <p style={{ color: '#94A3B8', lineHeight: '1.6', marginBottom: '1.5rem' }}>
            {data.wantsToSubmitProject
              ? "You're on the list and we've received your project submission. We'll review it and be in touch."
              : "You're on the list. We'll be in touch with updates, early access, and next steps."}
          </p>
          <button className="indi-btn indi-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="indi-card"
        style={{ maxWidth: '540px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.375rem', fontWeight: '700' }}>
            Join the Waitlist
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ color: '#94A3B8', fontSize: '0.9375rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
          Be among the first filmmakers and supporters on INDI.
          We'll notify you as we approach launch.
        </p>

        <form onSubmit={(e) => onSubmit(e, data.wantsToSubmitProject ? 'filmmaker' : 'waitlist', data)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input className="indi-input" style={inputStyle} value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="Your name" required />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input className="indi-input" style={inputStyle} type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} placeholder="your@email.com" required />
            </div>
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>What brings you to INDI?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {[
                { id: 'filmmaker', label: "I'm a filmmaker", color: '#84CC16' },
                { id: 'supporter', label: "I'm interested in supporting independent film", color: '#06B6D4' },
                { id: 'curious', label: "I'm just curious", color: '#94A3B8' },
              ].map((option) => (
                <label
                  key={option.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.65rem',
                    padding: '0.6rem 0.85rem',
                    background: data.roles.includes(option.id) ? 'rgba(6, 182, 212, 0.06)' : 'rgba(15, 23, 42, 0.5)',
                    border: data.roles.includes(option.id) ? '1px solid rgba(6, 182, 212, 0.2)' : '1px solid rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: data.roles.includes(option.id) ? '#E8ECF1' : '#B0B8C8',
                    transition: 'all 200ms ease',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={data.roles.includes(option.id)}
                    onChange={() => toggleRole(option.id)}
                    style={{ accentColor: option.color, width: '16px', height: '16px' }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {data.roles.includes('filmmaker') && (
            <div style={{
              padding: '1rem',
              background: 'rgba(132, 204, 22, 0.05)',
              border: '1px solid rgba(132, 204, 22, 0.15)',
              borderRadius: '10px',
              marginBottom: '1.25rem',
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.65rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: '#84CC16',
                fontWeight: '500',
              }}>
                <input
                  type="checkbox"
                  checked={data.wantsToSubmitProject}
                  onChange={(e) => setData({ ...data, wantsToSubmitProject: e.target.checked })}
                  style={{ accentColor: '#84CC16', width: '16px', height: '16px' }}
                />
                I'd like to submit a project for consideration
              </label>

              {data.wantsToSubmitProject && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={labelStyle}>Portfolio / Website</label>
                    <input className="indi-input" style={inputStyle} value={data.portfolio} onChange={(e) => setData({ ...data, portfolio: e.target.value })} placeholder="https://yourportfolio.com" />
                  </div>

                  {data.projects.map((project, index) => (
                    <div key={index} style={{
                      padding: '1rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid rgba(132, 204, 22, 0.1)',
                      borderRadius: '8px',
                      marginBottom: '0.75rem',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#84CC16' }}>
                          Project {index + 1}
                        </span>
                        {data.projects.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeProject(index)}
                            style={{ background: 'none', border: 'none', color: '#64748B', fontSize: '0.75rem', cursor: 'pointer' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={labelStyle}>Project Title *</label>
                        <input
                          className="indi-input"
                          style={inputStyle}
                          value={project.projectTitle}
                          onChange={(e) => updateProject(index, 'projectTitle', e.target.value)}
                          placeholder="My Film Title"
                          required={data.wantsToSubmitProject}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div>
                          <label style={labelStyle}>Genre</label>
                          <select
                            className="indi-input"
                            style={inputStyle}
                            value={project.genre}
                            onChange={(e) => updateProject(index, 'genre', e.target.value)}
                          >
                            <option value="">Select genre</option>
                            {['Drama', 'Comedy', 'Thriller', 'Horror', 'Sci-Fi', 'Documentary', 'Animation', 'Action', 'Romance', 'Other'].map(g => (
                              <option key={g} value={g}>{g}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Estimated Budget</label>
                          <select
                            className="indi-input"
                            style={inputStyle}
                            value={project.budget}
                            onChange={(e) => updateProject(index, 'budget', e.target.value)}
                          >
                            <option value="">Select range</option>
                            <option value="Under $50K">Under $50K</option>
                            <option value="$50K - $250K">$50K - $250K</option>
                            <option value="$250K - $1M">$250K - $1M</option>
                            <option value="$1M - $5M">$1M - $5M</option>
                            <option value="$5M+">$5M+</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label style={labelStyle}>Synopsis / Logline</label>
                        <textarea
                          className="indi-input"
                          style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                          value={project.synopsis}
                          onChange={(e) => updateProject(index, 'synopsis', e.target.value)}
                          placeholder="Brief description of your project..."
                        />
                      </div>
                    </div>
                  ))}

                  {data.projects.length < 3 && (
                    <button
                      type="button"
                      onClick={addProject}
                      style={{
                        background: 'none',
                        border: '1px dashed rgba(132, 204, 22, 0.3)',
                        borderRadius: '8px',
                        color: '#84CC16',
                        fontSize: '0.8125rem',
                        padding: '0.6rem',
                        width: '100%',
                        cursor: 'pointer',
                        marginBottom: '0.5rem',
                      }}
                    >
                      + Add Another Project ({data.projects.length}/3)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <button className="indi-btn indi-btn-primary" type="submit" style={{ width: '100%', justifyContent: 'center' }}>
            {data.wantsToSubmitProject ? 'Join Waitlist & Submit Project' : 'Join the Waitlist'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Filmmaker Application Modal
// ──────────────────────────────────────────────

function FilmmakerModal({ onClose, onSubmit, submitted }) {
  const emptyProject = { projectTitle: '', genre: '', budget: '', synopsis: '' };
  const [data, setData] = useState({
    name: '', email: '', portfolio: '',
    projects: [{ ...emptyProject }],
  });

  const addProject = () => {
    if (data.projects.length < 3) {
      setData({ ...data, projects: [...data.projects, { ...emptyProject }] });
    }
  };

  const removeProject = (index) => {
    if (data.projects.length > 1) {
      setData({ ...data, projects: data.projects.filter((_, i) => i !== index) });
    }
  };

  const updateProject = (index, field, value) => {
    const updated = [...data.projects];
    updated[index] = { ...updated[index], [field]: value };
    setData({ ...data, projects: updated });
  };

  const labelStyle = { display: 'block', fontSize: '0.8125rem', color: '#94A3B8', marginBottom: '0.35rem', fontWeight: '600' };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="indi-card" style={{ maxWidth: '500px', width: '100%', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
          <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.375rem', fontWeight: '700', marginBottom: '0.5rem' }}>Application Received</h3>
          <p style={{ color: '#94A3B8', lineHeight: '1.6', marginBottom: '1.5rem' }}>
            Thanks for your interest in INDI. Our team will review your submission and reach out within 5 business days.
          </p>
          <button className="indi-btn indi-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="indi-card"
        style={{ maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.375rem', fontWeight: '700' }}>
            Filmmaker Application
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ color: '#94A3B8', fontSize: '0.9375rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
          Tell us about yourself and your project. We'll reach out to discuss how INDI can help bring your vision to life.
        </p>

        <form onSubmit={(e) => onSubmit(e, 'filmmaker', data)}>
          {/* Personal Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input className="indi-input" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} required />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input className="indi-input" type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} required />
            </div>
          </div>
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Portfolio / IMDb URL</label>
            <input className="indi-input" value={data.portfolio} onChange={(e) => setData({ ...data, portfolio: e.target.value })} placeholder="https://..." />
          </div>

          {/* Projects */}
          {data.projects.map((project, i) => (
            <div key={i} style={{
              padding: '1.25rem',
              background: 'rgba(6, 182, 212, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: '12px',
              marginBottom: '0.75rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.75rem',
                  color: i === 0 ? '#84CC16' : '#06B6D4',
                  fontWeight: '500',
                }}>
                  {i === 0 ? 'Primary Project' : `Additional Project ${i}`}
                </span>
                {i > 0 && (
                  <button
                    type="button"
                    onClick={() => removeProject(i)}
                    style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '0.8125rem', cursor: 'pointer', fontFamily: "'Instrument Sans', sans-serif" }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={labelStyle}>Project Title *</label>
                <input className="indi-input" value={project.projectTitle} onChange={(e) => updateProject(i, 'projectTitle', e.target.value)} placeholder="Working title of your project" required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={labelStyle}>Genre *</label>
                  <input className="indi-input" value={project.genre} onChange={(e) => updateProject(i, 'genre', e.target.value)} placeholder="Drama, Thriller..." required />
                </div>
                <div>
                  <label style={labelStyle}>Estimated Budget</label>
                  <input className="indi-input" value={project.budget} onChange={(e) => updateProject(i, 'budget', e.target.value)} placeholder="$50,000 – $500,000" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Brief Synopsis *</label>
                <textarea className="indi-input indi-textarea" value={project.synopsis} onChange={(e) => updateProject(i, 'synopsis', e.target.value)} placeholder="Tell us about your project in a few sentences..." required />
              </div>
            </div>
          ))}

          {data.projects.length < 3 && (
            <button
              type="button"
              onClick={addProject}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.6rem',
                background: 'rgba(6, 182, 212, 0.06)',
                border: '1px dashed rgba(6, 182, 212, 0.25)',
                borderRadius: '10px',
                color: '#06B6D4',
                fontFamily: "'Instrument Sans', sans-serif",
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                marginBottom: '1.25rem',
                transition: 'all 200ms ease',
              }}
            >
              + Add Another Project ({3 - data.projects.length} remaining)
            </button>
          )}

          <button className="indi-btn indi-btn-lime" type="submit" style={{ width: '100%', justifyContent: 'center' }}>
            Submit Application
          </button>
        </form>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Investor Interest Modal
// ──────────────────────────────────────────────

function InvestorModal({ onClose, onSubmit, submitted }) {
  const [data, setData] = useState({
    name: '', email: '', investmentRange: '', filmInvestmentExperience: '', accredited: '',
  });

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="indi-card" style={{ maxWidth: '500px', width: '100%', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
          <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.375rem', fontWeight: '700', marginBottom: '0.5rem' }}>Interest Registered</h3>
          <p style={{ color: '#94A3B8', lineHeight: '1.6', marginBottom: '1.5rem' }}>
            Thanks for your interest in investing through INDI. We'll notify you as soon as investment opportunities are available.
          </p>
          <button className="indi-btn indi-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="indi-card"
        style={{ maxWidth: '560px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.375rem', fontWeight: '700' }}>
            Investor Interest
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ color: '#94A3B8', fontSize: '0.9375rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
          Register your interest to be among the first investors on INDI. 
          No commitment — we'll reach out when opportunities are available.
        </p>

        <form onSubmit={(e) => onSubmit(e, 'investor', data)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8125rem', color: '#94A3B8', marginBottom: '0.35rem', fontWeight: '600' }}>Full Name *</label>
              <input className="indi-input" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8125rem', color: '#94A3B8', marginBottom: '0.35rem', fontWeight: '600' }}>Email *</label>
              <input className="indi-input" type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} required />
            </div>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8125rem', color: '#94A3B8', marginBottom: '0.35rem', fontWeight: '600' }}>Investment Range *</label>
            <select
              className="indi-input"
              value={data.investmentRange}
              onChange={(e) => setData({ ...data, investmentRange: e.target.value })}
              required
              style={{ cursor: 'pointer', appearance: 'none' }}
            >
              <option value="">Select range...</option>
              <option value="50-999">$50 – $999</option>
              <option value="1000-5000">$1,000 – $5,000</option>
              <option value="5000-25000">$5,000 – $25,000</option>
              <option value="25000-100000">$25,000 – $100,000</option>
              <option value="100000+">$100,000+</option>
            </select>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8125rem', color: '#94A3B8', marginBottom: '0.35rem', fontWeight: '600' }}>Have you ever invested in films? *</label>
            <select
              className="indi-input"
              value={data.filmInvestmentExperience}
              onChange={(e) => setData({ ...data, filmInvestmentExperience: e.target.value })}
              required
              style={{ cursor: 'pointer', appearance: 'none' }}
            >
              <option value="">Select...</option>
              <option value="yes-multiple">Yes — multiple times</option>
              <option value="yes-once">Yes — once</option>
              <option value="no-interested">No, but I'm interested</option>
              <option value="no-other">No — but I invest in other alternative assets</option>
            </select>
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8125rem', color: '#94A3B8', marginBottom: '0.35rem', fontWeight: '600' }}>Are you an accredited investor?</label>
            <select
              className="indi-input"
              value={data.accredited}
              onChange={(e) => setData({ ...data, accredited: e.target.value })}
              style={{ cursor: 'pointer', appearance: 'none' }}
            >
              <option value="">Select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="unsure">Not sure</option>
            </select>
            <div style={{ fontSize: '0.75rem', color: '#4A5568', marginTop: '0.35rem' }}>
              Accreditation is not required to invest under Reg CF.
            </div>
          </div>
          <button className="indi-btn indi-btn-primary" type="submit" style={{ width: '100%', justifyContent: 'center' }}>
            Register Interest
          </button>
        </form>
      </div>
    </div>
  );
}
